import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, collection, getDocs, onSnapshot, query, where, addDoc, serverTimestamp, updateDoc, arrayUnion } from "firebase/firestore";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";

import { ArrowLeft, Info, Package, ListChecks, FileText, LayoutGrid, Printer, Archive, Layers, HardHat, Truck, AlertCircle, BrainCircuit, ChevronUp, ChevronDown, Sparkles, X, Loader2, CheckCircle, AlertTriangle, UploadCloud, Scissors } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { buildJobTicketPdf } from '../lib/jobTicket';
import { buildJobLabelPdf } from '../lib/jobLabel';
import SendComponentsToCatalogModal from '../components/JobView/SendComponentsToCatalogModal';
import InvoiceModal from '../components/JobView/InvoiceModal';

// --- Importamos los componentes de las pestañas ---
import GeneralInfoDisplayTab from '../components/JobView/Tabs/GeneralInfoDisplayTab';
import ProductDetailsDisplayTab from '../components/JobView/Tabs/ProductDetailsDisplayTab';
import TasksAndChecklistTab from '../components/JobView/Tabs/TasksAndChecklistTab';
import FilesAndDocsDisplayTab from '../components/JobView/Tabs/FilesAndDocsDisplayTab';
import FileArtifactsTab from '../components/JobView/Tabs/FileArtifactsTab';
import GangRunTab from '../components/JobView/Tabs/GangRunTab';
import DesignManagerTab from '../components/JobView/Tabs/DesignManagerTab';
import PrepressTab from '../components/JobView/Tabs/PrepressTab';
import ConstructionTab from '../components/JobView/Tabs/ConstructionTab';
import InstallationTab from '../components/JobView/Tabs/InstallationTab';
import PiecesInventoryTab from '../components/JobView/Tabs/PiecesInventoryTab';
import JobAiChatModal from '../components/JobView/JobAiChatModal';
import SheetDetailModal from '../components/JobView/Tabs/SheetDetailModal';

const ImageModal = ({ imageUrl, onClose }) => {
    if (!imageUrl) return null;
    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
            onClick={onClose}
        >
            <button 
                className="absolute top-4 right-4 text-white hover:text-gray-300"
                onClick={onClose}
            >
                <X size={32} />
            </button>
            <img 
                src={imageUrl} 
                alt="Vista maximizada" 
                className="max-h-[90vh] max-w-[90vw] object-contain"
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    );
};


const ProgressBar = ({ label, value, total }) => (
    <div>
        <div className="flex justify-between text-xs mb-0.5">
            <span className="font-semibold text-gray-600">{label}</span>
            <span className="text-gray-500">{value} / {total}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="bg-indigo-600 h-1.5 rounded-full" style={{ width: total > 0 ? `${(value / total) * 100}%` : '0%' }}></div>
        </div>
    </div>
);

const JobDashboardHeader = ({ stats, isLoading, jobData, functions, db, onShowMissingFileSheet, onOpenGeneralAIChat, onOpenJobChat }) => {
    const STATUS_COLORS = { 'Diseño': '#3b82f6', 'Prepress': '#f59e0b', 'Producción': '#10b981', 'Devuelto': '#ef4444' };
    const MATERIAL_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#00C49F'];
    
    const [aiSummary, setAiSummary] = useState("Presiona 'Generar' para obtener un resumen del estado actual.");
    const [isGenerating, setIsGenerating] = useState(false);

    // Llamamos a la Cloud Function (Vertex AI) — la API key ya NO vive en el frontend.
    const handleGenerateSummary = async () => {
        setIsGenerating(true);
        setAiSummary("Contactando a la IA, por favor espera...");
        try {
            const generateSummary = httpsCallable(functions, 'generateJobSummary');
            const result = await generateSummary({
                jobId: jobData.id,
                jobName: jobData.jobName,
                stats: stats,
            });
            setAiSummary(result.data?.summary || "La IA no pudo generar un resumen.");
        } catch (error) {
            console.error("Error generating AI summary:", error);
            setAiSummary(`Error al contactar la IA: ${error.message}.`);
        } finally {
            setIsGenerating(false);
        }
    };


    const phase = jobData?.status?.phase || jobData?.status?.stage || '—';
    const PHASE_CLS = {
        'Finalizado': 'bg-green-100 text-green-700',
        'Pendiente por Facturar': 'bg-purple-100 text-purple-700',
        'En Hold': 'bg-yellow-100 text-yellow-700', 'Hold': 'bg-yellow-100 text-yellow-700',
        'Devuelto a Diseño': 'bg-red-100 text-red-700',
    };
    const statusCls = PHASE_CLS[phase] || 'bg-blue-100 text-blue-700';
    const pct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);
    const piezasPct = pct(stats.piezasListas, stats.totalPiezas);
    let deliveryText = '—', deliveryBadge = null;
    if (jobData?.deliveryDate) {
        deliveryText = jobData.deliveryDate;
        const dd = new Date(`${jobData.deliveryDate}T00:00:00`);
        const done = phase === 'Finalizado' || phase === 'Pendiente por Facturar';
        if (!isNaN(dd) && !done) {
            const days = Math.ceil((dd - new Date()) / 86400000);
            if (days < 0) deliveryBadge = <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">vencida</span>;
            else if (days <= 3) deliveryBadge = <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">en {days} día{days === 1 ? '' : 's'}</span>;
            else deliveryBadge = <span className="text-xs text-gray-400">en {days} días</span>;
        }
    }
    const tiles = [
        { Icon: Layers, color: 'text-blue-500', label: 'Planchas', value: stats.totalPlanchasRepeticiones },
        { Icon: Printer, color: 'text-indigo-500', label: '1 Cara', value: stats.impresion1Cara.total },
        { Icon: Printer, color: 'text-indigo-400', label: '2 Caras', value: stats.impresion2Caras.total },
        { Icon: Scissors, color: 'text-rose-500', label: 'Solo Corte', value: stats.soloCorte.total },
        { Icon: Package, color: 'text-amber-500', label: 'Piezas', value: stats.totalPiezas },
        { Icon: CheckCircle, color: 'text-green-500', label: 'Piezas listas', value: stats.piezasListas, sub: `/${stats.totalPiezas} (${piezasPct}%)` },
    ];
    const prodBars = [
        { label: 'Impresión', v: stats.planchasImpresas, t: stats.totalPlanchasImpresion, c: 'bg-blue-500' },
        { label: 'Corte', v: stats.planchasCortadas, t: stats.totalPlanchasCorte, c: 'bg-rose-500' },
    ];

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg shadow-sm px-4 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Estado</span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${statusCls}`}>
                    <span className="w-2 h-2 rounded-full bg-current opacity-70" /> {phase}
                </span>
                <div className="ml-auto flex items-center gap-2 text-sm">
                    <span className="text-gray-400">Entrega:</span>
                    <span className="font-semibold text-gray-700">{deliveryText}</span>
                    {deliveryBadge}
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {tiles.map((t, i) => (
                    <div key={i} className="bg-white rounded-lg shadow-sm p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            <t.Icon size={13} className={t.color} /> {t.label}
                        </div>
                        <div className="text-2xl font-bold text-gray-800 mt-1 leading-none">
                            {t.value}{t.sub && <span className="text-xs font-normal text-gray-400 ml-1">{t.sub}</span>}
                        </div>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-3">
                    {/* Ventana dividida: barras a la izquierda · materiales + censo a la derecha */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:divide-x divide-gray-100">
                        <div className="md:pr-4">
                            <h3 className="font-bold text-gray-800 text-sm mb-2">Progreso de Producción</h3>
                            {prodBars.map((b, i) => (
                                <div key={i} className="mb-2 max-w-[16rem]">
                                    <div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>{b.label}</span><span>{b.v}/{b.t} · {pct(b.v, b.t)}%</span></div>
                                    <div className="w-full bg-gray-200 rounded-full h-2"><div className={`${b.c} h-2 rounded-full transition-all`} style={{ width: `${pct(b.v, b.t)}%` }} /></div>
                                </div>
                            ))}
                        </div>
                        <div className="md:pl-4 pt-3 md:pt-0 border-t md:border-t-0 border-gray-100 space-y-3">
                            <div>
                                <h3 className="font-bold text-gray-800 text-sm mb-1.5">Materiales</h3>
                                {(stats.materialSummaryForKpi || []).length === 0 ? (
                                    <p className="text-xs text-gray-400">Sin materiales asignados</p>
                                ) : (
                                    <ul className="space-y-1">
                                        {stats.materialSummaryForKpi.map((m) => (
                                            <li key={m.name} className="flex items-center justify-between gap-2 text-xs">
                                                <span className="truncate text-gray-600" title={m.name}>{m.name}</span>
                                                <span className="text-gray-500 whitespace-nowrap tabular-nums">{m.count} · {Math.round(m.percentage)}%</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="pt-2 border-t border-gray-100 text-xs text-gray-600 space-y-1">
                                <div className="flex items-center justify-between">
                                    <span>Archivos de Diseño</span>
                                    <span><b>{stats.totalPlanchasRepeticiones - stats.planchasSinArchivo}/{stats.totalPlanchasRepeticiones}</b>
                                        {stats.planchasSinArchivo > 0 && <button onClick={onShowMissingFileSheet} className="ml-1 text-red-600 underline">({stats.planchasSinArchivo} sin archivo)</button>}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between"><span>En Prepress</span><b>{stats.planchasEnPrepress}</b></div>
                                <div className="flex items-center justify-between"><span>Tareas</span><b>{stats.tareasCompletadas}/{stats.tareasAbiertas + stats.tareasCompletadas}</b></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-3 flex flex-col">
                    <button onClick={onOpenJobChat} className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700">
                        <BrainCircuit size={16} /> Asistente del Job
                    </button>
                    <div className="flex justify-between items-center mb-1">
                        <h3 className="font-bold text-gray-800 text-sm flex items-center gap-1"><Sparkles size={15} /> Resumen IA</h3>
                        <button onClick={handleGenerateSummary} disabled={isGenerating || isLoading} className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:bg-purple-400">
                            <Sparkles size={13} /> {isGenerating ? 'Generando…' : 'Generar'}
                        </button>
                    </div>
                    <p className="text-gray-600 text-xs overflow-y-auto max-h-28 pr-1">{aiSummary}</p>
                </div>
            </div>
        </div>
    );
};
const KPI = ({ label, value, breakdown = [], single = false, progressData = null, isTotalPlanchasCard = false }) => {
    const containerClasses = `bg-white p-3 rounded-lg shadow-sm flex flex-col ${!isTotalPlanchasCard ? 'justify-between h-full' : ''}`;
    return (
        <div className={containerClasses}>
            <div>
                <div className="flex justify-between items-center">
                    <p className="text-md font-semibold text-gray-700">{label}</p>
                    <span className="text-3xl font-bold text-gray-800">{value}</span>
                </div>
            </div>
            {breakdown.length > 0 && (
                <ul className="text-xs text-gray-500 mt-1.5 space-y-1 pt-1.5 border-t border-gray-100">
                    {breakdown.map((item) => (
                        <li key={item.name} className="grid grid-cols-12 items-center">
                            <span className="truncate pr-2 col-span-7" title={item.name}>{item.name}:</span> 
                            <span className="font-medium text-right col-span-2">{item.count}</span>
                            <span className="font-medium text-right text-gray-400 col-span-3">{item.percentage.toFixed(0)}%</span>
                        </li>
                    ))}
                </ul>
            )}
            {progressData && (
                 <div className="mt-1 pt-2 border-t border-gray-100 space-y-2">
                    <h4 className="font-bold text-gray-700 text-xs">Progreso de Producción</h4>
                    <ProgressBar label="Impresión" value={progressData.impresion.value} total={progressData.impresion.total} />
                    <ProgressBar label="Corte" value={progressData.corte.value} total={progressData.corte.total} />
                </div>
            )}
        </div>
    );
};
const ProgressItem = ({ label, value, total, alertValue, alertLabel, onClick }) => {
    const hasAction = !!onClick;
    return (
        <div className={`flex justify-between items-center text-sm ${hasAction ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded-md' : ''}`} onClick={onClick}>
            <span className="font-semibold text-gray-600">{label}</span>
            <div className="flex items-center">
                {alertValue > 0 && 
                    <div className="flex items-center gap-1 mr-2">
                        <AlertCircle size={14} className="text-red-500" />
                        <span className="text-xs font-semibold text-red-500">{`(${alertValue} ${alertLabel})`}</span>
                    </div>
                }
                <span className="text-gray-800 font-bold">{value}{total ? ` / ${total}` : ''}</span>
            </div>
        </div>
    );
};


// Widget flotante de subidas: vive a nivel de JobView para que el progreso siga
// visible aunque se cambie de pestaña (subida en segundo plano).
const UploadsWidget = ({ uploads, onDismiss, onClearDone }) => {
  if (!uploads || uploads.length === 0) return null;
  const doneCount = uploads.filter(u => u.status === 'done').length;
  return (
    <div className="fixed bottom-4 right-4 z-40 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800 text-white">
        <span className="text-sm font-semibold flex items-center gap-2">
          <UploadCloud size={16} /> Subida de archivos
        </span>
        {doneCount > 0 && (
          <button onClick={onClearDone} className="text-xs text-gray-300 hover:text-white">Limpiar</button>
        )}
      </div>
      <div className="max-h-64 overflow-y-auto divide-y">
        {uploads.map((u) => (
          <div key={u.id} className="px-4 py-2.5">
            <div className="flex items-center gap-2">
              {u.status === 'done' && <CheckCircle size={15} className="text-emerald-500 flex-shrink-0" />}
              {u.status === 'error' && <AlertTriangle size={15} className="text-red-500 flex-shrink-0" />}
              {u.status === 'uploading' && <Loader2 size={15} className="text-indigo-500 animate-spin flex-shrink-0" />}
              <span className="text-xs font-medium text-gray-700 truncate flex-grow" title={u.name}>{u.name}</span>
              <span className="text-xs font-semibold text-gray-500">
                {u.status === 'done' ? 'Listo' : u.status === 'error' ? 'Error' : `${Math.round(u.progress)}%`}
              </span>
              {(u.status === 'done' || u.status === 'error') && (
                <button onClick={() => onDismiss(u.id)} className="text-gray-300 hover:text-gray-600"><X size={14} /></button>
              )}
            </div>
            {u.status === 'uploading' && (
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5 overflow-hidden">
                <div className="bg-indigo-600 h-1.5 rounded-full transition-all duration-200" style={{ width: `${u.progress}%` }} />
              </div>
            )}
            {u.status === 'error' && <p className="text-xs text-red-500 mt-0.5">{u.error}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

const JobView = ({ user, db, storage, onOpenGeneralAIChat }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const functions = getFunctions();

  const [jobData, setJobData] = useState(null);
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [isJobChatOpen, setIsJobChatOpen] = useState(false); // asistente IA del Job
  // Pestaña inicial: si el correo trae ?tab=checklist abre directo en "Tareas".
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'design');
  const [materialsCatalog, setMaterialsCatalog] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCockpitVisible, setIsCockpitVisible] = useState(true);
  const [maximizedImageUrl, setMaximizedImageUrl] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [initialSheetToShow, setInitialSheetToShow] = useState(null);
  const [sheetForDetails, setSheetForDetails] = useState(null);
  // Cola de subidas de archivos: [{ id, name, progress, status, error, task }]
  const [docUploads, setDocUploads] = useState([]);
  const uploadSeqRef = useRef(0);

  // Sube un archivo a Storage con progreso en vivo y, al terminar, agrega su
  // metadato al array `documents` del trabajo en Firestore (subida en segundo plano).
  const handleUploadDocument = (file, meta = {}) => {
    if (!file || !storage || !jobId) return;
    const id = `up_${Date.now()}_${++uploadSeqRef.current}`;
    const path = `jobs/${jobId}/docs/${Date.now()}_${file.name}`;
    const fileRef = storageRef(storage, path);
    const task = uploadBytesResumable(fileRef, file, { contentType: file.type || undefined });

    setDocUploads(prev => [...prev, { id, name: file.name, progress: 0, status: 'uploading', error: null, task }]);

    task.on('state_changed',
      (snapshot) => {
        const pct = snapshot.totalBytes ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100 : 0;
        setDocUploads(prev => prev.map(u => u.id === id ? { ...u, progress: pct } : u));
      },
      (error) => {
        console.error('Error subiendo archivo:', error);
        setDocUploads(prev => prev.map(u => u.id === id ? { ...u, status: 'error', error: error?.message || 'Fallo de subida' } : u));
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          const docMeta = {
            id,
            name: file.name,
            description: meta.description || '',
            category: meta.category || 'otro',
            url,
            storagePath: path,
            size: file.size || 0,
            contentType: file.type || '',
            uploadedAt: Date.now(),
            uploadedByUid: user?.uid || '',
            uploadedByName: user?.displayName || user?.email || '',
          };
          await updateDoc(doc(db, 'jobs', jobId), { documents: arrayUnion(docMeta) });
          setDocUploads(prev => prev.map(u => u.id === id ? { ...u, status: 'done', progress: 100 } : u));
          // Auto-retira la fila "Listo" del widget tras unos segundos.
          setTimeout(() => setDocUploads(prev => prev.filter(u => u.id !== id)), 5000);
        } catch (error) {
          console.error('Error guardando metadato del archivo:', error);
          setDocUploads(prev => prev.map(u => u.id === id ? { ...u, status: 'error', error: 'Subido, pero no se pudo registrar.' } : u));
        }
      }
    );
  };

  const handleDeleteDocument = async (index) => {
    const documents = (jobData?.documents || []).filter(Boolean);
    const target = documents[index];
    if (!target) return;
    const newDocs = documents.filter((_, i) => i !== index);
    try {
      await updateDoc(doc(db, 'jobs', jobId), { documents: newDocs });
      if (target.storagePath) {
        try { await deleteObject(storageRef(storage, target.storagePath)); }
        catch (e) { console.warn('No se pudo borrar el archivo de Storage:', e); }
      }
    } catch (error) {
      console.error('Error al eliminar el documento:', error);
      alert('No se pudo eliminar el archivo. Inténtalo de nuevo.');
    }
  };

  const handleUpdateDocument = async (index, patch) => {
    const documents = (jobData?.documents || []).filter(Boolean);
    if (!documents[index]) return;
    const newDocs = documents.map((d, i) => i === index ? { ...d, ...patch } : d);
    try {
      await updateDoc(doc(db, 'jobs', jobId), { documents: newDocs });
    } catch (error) {
      console.error('Error al actualizar el documento:', error);
      alert('No se pudo actualizar el archivo.');
    }
  };

  const dismissUpload = (id) => setDocUploads(prev => prev.filter(u => u.id !== id));
  const clearDoneUploads = () => setDocUploads(prev => prev.filter(u => u.status !== 'done'));

  useEffect(() => {
    if (!db || !jobId) {
        setError("ID de trabajo inválido.");
        setIsLoading(false);
        return;
    }
    
    setIsLoading(true);

    const loadCatalogs = async () => {
        try {
            const materialsSnapshot = await getDocs(collection(db, "materials"));
            setMaterialsCatalog(materialsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            const clientsSnapshot = await getDocs(collection(db, "clients"));
            setClients(clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            console.error("Error loading catalogs:", error);
            setError("Error al cargar datos de soporte.");
        }
    };
    loadCatalogs();

    const jobDocRef = doc(db, "jobs", jobId);
    const unsubscribeJob = onSnapshot(jobDocRef, async (docSnap) => {
        if (docSnap.exists()) {
            const currentJobData = { id: docSnap.id, ...docSnap.data() };
            setJobData(currentJobData);
            setError(null);
            
            if (currentJobData.assignedRoles && currentJobData.assignedRoles.length > 0) {
                try {
                    const usersRef = collection(db, "users");
                    const q = query(usersRef, where("roles", "array-contains-any", currentJobData.assignedRoles));
                    const usersSnapshot = await getDocs(q);
                    setTeamMembers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                } catch (error) {
                    console.error("Error fetching team members:", error);
                }
            } else {
                setTeamMembers([]);
            }

        } else {
            setError("El trabajo no fue encontrado.");
            setJobData(null);
        }
        setIsLoading(false);
    }, (err) => {
        console.error("Error fetching job:", err);
        setError("Error de conexión al cargar el trabajo.");
        setIsLoading(false);
    });

    const sheetsQuery = query(collection(db, `jobs/${jobId}/sheets`));
    const unsubscribeSheets = onSnapshot(sheetsQuery, (querySnapshot) => {
        const sheetsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSheets(sheetsData);
    }, (error) => { console.error("Error fetching sheets:", error); });
    
    const tasksQuery = query(collection(db, `jobs/${jobId}/tasks`));
    const unsubscribeTasks = onSnapshot(tasksQuery, (querySnapshot) => {
        const tasksData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTasks(tasksData);
    }, (error) => { console.error("Error fetching tasks:", error); });


    return () => { 
        unsubscribeJob();
        unsubscribeSheets(); 
        unsubscribeTasks();
    };
  }, [db, jobId]);

  useEffect(() => {
      if (jobData?.clientId && clients.length > 0) {
          const clientData = clients.find(c => c.id === jobData.clientId);
          setClient(clientData || { name: "Cliente no encontrado" });
      }
  }, [jobData, clients]);

  const handleShowMissingFileSheet = () => {
      const sheetWithoutFile = sheets.find(s => !s.designFileFront?.url && !s.designFileBack?.url);
      if (sheetWithoutFile) {
          setInitialSheetToShow(sheetWithoutFile);
          setActiveTab('design');
      }
  };

  // Enviar componentes del trabajo al Catálogo de Productos (SKUs reutilizables).
  const [showSendComponents, setShowSendComponents] = useState(false);
  const hasComponentsForCatalog = (jobData?.components || []).some((c) => (c?.name || "").trim() && (c?.code || "").trim());

  // Facturación (solo Admin / rol Administración) + estado/etiqueta.
  const canInvoice = user?.permissions?.canManageInvoicing || user?.roles?.includes('Admin');
  const [showInvoice, setShowInvoice] = useState(false);
  const jobPhase = jobData?.status?.phase;
  const isBilledOrDone = jobPhase === 'Pendiente por Facturar' || jobPhase === 'Finalizado';
  const handleMarkBilling = async () => {
    if (!jobData) return;
    if (!window.confirm('¿Marcar el trabajo como TERMINADO y enviarlo a "Pendiente por Facturar"?')) return;
    try { await updateDoc(doc(db, 'jobs', jobId), { 'status.phase': 'Pendiente por Facturar', 'status.stage': 'PENDIENTE POR FACTURAR' }); }
    catch (e) { alert('No se pudo actualizar el estado: ' + (e?.message || e)); }
  };
  const handleDownloadLabel = async () => {
    try { await buildJobLabelPdf({ jobId, jobData, client, sheets }); }
    catch (e) { alert('No se pudo generar la etiqueta: ' + (e?.message || e)); }
  };
  // Genera el Job Ticket (PDF) y lo sube a Storage; devuelve la URL para adjuntarlo
  // al correo de finalización (lo hace el InvoiceModal antes de marcar Finalizado).
  const uploadJobTicketForEmail = async () => {
    let artifacts = [];
    try { const snap = await getDocs(collection(db, `jobs/${jobId}/artifacts`)); artifacts = snap.docs.map((d) => ({ id: d.id, ...d.data() })); } catch (_) {}
    const blob = await buildJobTicketPdf({ jobId, jobData, client, sheets, tasks, artifacts, materialsCatalog, output: 'blob' });
    const r = storageRef(storage, `jobs/${jobId}/JOB-TICKET_${jobId}.pdf`);
    await new Promise((res, rej) => { const t = uploadBytesResumable(r, blob, { contentType: 'application/pdf' }); t.on('state_changed', null, rej, res); });
    return getDownloadURL(r);
  };

  // Job Ticket (PDF) — hoja viajera legible para consulta offline / backup (Fase A).
  const [ticketBusy, setTicketBusy] = useState(false);
  const handleDownloadJobTicket = async () => {
      if (!jobData) return;
      setTicketBusy(true);
      try {
          // Los artefactos no se cargan de continuo: se traen al generar el ticket.
          let artifacts = [];
          try {
              const snap = await getDocs(collection(db, `jobs/${jobId}/artifacts`));
              artifacts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          } catch (_) { /* sin artefactos */ }
          await buildJobTicketPdf({ jobId, jobData, client, sheets, tasks, artifacts, materialsCatalog });
      } catch (e) {
          console.error('Error generando Job Ticket:', e);
          alert('No se pudo generar el Job Ticket: ' + (e?.message || e));
      } finally { setTicketBusy(false); }
  };

  const dashboardStats = useMemo(() => {
    if (!jobData || materialsCatalog.length === 0) {
        return { 
            statusSummary: {}, 
            impresion1Cara: {breakdown:[], total: 0}, 
            impresion2Caras: {breakdown:[], total: 0}, 
            soloCorte: {breakdown:[], total: 0}, 
            materialSummaryForKpi: [], 
            materialSummaryForChart: [], 
            statusSummaryForChart: [],
            totalPiezas: 0,
            piezasListas: 0,
            planchasSinArchivo: 0,
            planchasImpresas: 0,
            planchasCortadas: 0,
            totalPlanchasImpresion: 0,
            totalPlanchasCorte: 0,
            totalPlanchasRepeticiones: 0,
            tareasAbiertas: 0,
            tareasCompletadas: 0,
            planchasEnPrepress: 0
        };
    }
    const planchasSinArchivo = sheets.filter(sheet => !sheet.designFileFront?.url && !sheet.designFileBack?.url).length;

    // Jobs viejos terminados sin marcar las planchas (printedCount/cutCount en 0) mostraban 0%.
    // Si el job está cerrado (Finalizado / Pendiente por Facturar) se infiere producción completa.
    const _phase = jobData?.status?.phase || jobData?.status?.stage;
    const jobDone = _phase === 'Finalizado' || _phase === 'Pendiente por Facturar';
    const effPrinted = (s) => (jobDone && s.isPrinting ? (Number(s.repetitions) || 0) : (s.printedCount || 0));
    const effCut = (s) => (jobDone ? (Number(s.repetitions) || 0) : (s.cutCount || 0));

    const planchasImpresas = sheets.reduce((sum, sheet) => sum + effPrinted(sheet), 0);
    const planchasCortadas = sheets.reduce((sum, sheet) => sum + effCut(sheet), 0);
    
    const totalPlanchasImpresion = sheets.filter(sheet => sheet.isPrinting).reduce((sum, sheet) => sum + (Number(sheet.repetitions) || 0), 0);
    const totalPlanchasCorte = sheets.reduce((sum, sheet) => sum + (Number(sheet.repetitions) || 0), 0);
    
    const totalPlanchasRepeticiones = sheets.reduce((sum, sheet) => sum + (Number(sheet.repetitions) || 0), 0);
    const totalPiezas = sheets.reduce((sum, sheet) => { 
        const sheetRepetitions = Number(sheet.repetitions) || 0; // La variable se define aquí como 'sheetRepetitions'
        const piecesInSheet = (sheet.pieces || []).reduce((sheetSum, piece) => sheetSum + (Number(piece.qtyPerSheet) || 0), 0); 
        return sum + (piecesInSheet * sheetRepetitions); // Se corrige el uso aquí
    }, 0);
    
    const piezasListas = sheets.reduce((sum, sheet) => {
        const piecesInSheet = (sheet.pieces || []).reduce((pieceSum, piece) => pieceSum + (Number(piece.qtyPerSheet) || 0), 0);
        return sum + (piecesInSheet * effCut(sheet));
    }, 0);

    const calculateBreakdown = (filterFn) => { const filtered = sheets.filter(filterFn); const total = filtered.reduce((sum, sheet) => sum + (Number(sheet.repetitions) || 0), 0); const breakdownCounts = filtered.reduce((acc, sheet) => { const materialName = materialsCatalog.find(m => m.id === sheet.materialId)?.name || 'N/A'; acc[materialName] = (acc[materialName] || 0) + (Number(sheet.repetitions) || 0); return acc; }, {}); const breakdown = Object.entries(breakdownCounts).map(([name, count]) => ({ name, count, percentage: total > 0 ? (count / total) * 100 : 0, })); return { total, breakdown }; };
    // printFaces se guarda como NÚMERO (1 / 2). Antes se comparaba con strings ('1'/'2')
    // → los KPIs salían en 0. Se normaliza con Number() y, por robustez, toda plancha de
    // impresión que NO sea explícitamente 2 caras cuenta como 1 cara (sin huecos).
    const impresion2Caras = calculateBreakdown(sheet => sheet.isPrinting && Number(sheet.printFaces) === 2);
    const impresion1Cara = calculateBreakdown(sheet => sheet.isPrinting && Number(sheet.printFaces) !== 2);
    const soloCorte = calculateBreakdown(sheet => !sheet.isPrinting);
    
    let planchasEnPrepress = 0;
    const statusSummary = sheets.reduce((acc, sheet) => {
        const currentStatus = (sheet.status || '').toLowerCase();
        
        if (currentStatus === 'devuelto a diseño') {
            acc.Devuelto = (acc.Devuelto || 0) + 1;
        } else if (sheet.productionStatus || sheet.prepressStatus === 'Listo para Producción') {
            acc.Producción = (acc.Producción || 0) + 1;
        } else if (currentStatus === 'listo para prepress') {
            acc.Prepress = (acc.Prepress || 0) + 1;
            planchasEnPrepress++;
        } else {
            acc.Diseño = (acc.Diseño || 0) + 1;
        }
        return acc;
    }, { 'Diseño': 0, 'Prepress': 0, 'Producción': 0, 'Devuelto': 0 });

    const statusSummaryForChart = Object.entries(statusSummary).map(([name, value]) => ({ name: `${name} (${value})`, value })).filter(entry => entry.value > 0);
    const materialSummaryCounts = sheets.reduce((acc, sheet) => { const materialName = materialsCatalog.find(m => m.id === sheet.materialId)?.name || 'N/A'; acc[materialName] = (acc[materialName] || 0) + (Number(sheet.repetitions) || 0); return acc; }, {});
    const materialSummaryForKpi = Object.entries(materialSummaryCounts).map(([name, count]) => ({ name, count, percentage: totalPlanchasRepeticiones > 0 ? (count / totalPlanchasRepeticiones) * 100 : 0 }));
    const materialSummaryForChart = Object.entries(materialSummaryCounts).map(([name, value]) => ({ name: `${name} (${value})`, value }));

    const tareasAbiertas = tasks.filter(t => t.status !== 'completed').length;
    const tareasCompletadas = tasks.filter(t => t.status === 'completed').length;
    // NUEVO: tareas de sistema pendientes (corrección + reproducción) para alerta
    const tareasSistemaPendientes = tasks.filter(t => t.status !== 'completed' && (t.type === 'system' || t.type === 'system_reproduction')).length;
    // Parpadeo de pestañas según estado de las planchas:
    // - Diseño: ROJO si hay devueltas, AMARILLO si hay en hold.
    // - Prepress: AMARILLO si hay corregidas (re-revisión) o por reproducir.
    const planchasDevueltas = sheets.filter(s => s.status === 'Devuelto a Diseño').length;
    const planchasHold = sheets.filter(s => s.status === 'Hold' || s.status === 'En Hold').length;
    // "Reproducir" se detecta por tarea de reproducción pendiente (el estado mostrado, no sheet.status).
    const sheetIdsReproducir = new Set((tasks || []).filter(t => t.status !== 'completed' && t.type === 'system_reproduction').map(t => t.sheetId));
    const planchasParaRevisarPrepress = sheets.filter(s => s.status === 'Corregido por Diseño' || sheetIdsReproducir.has(s.id)).length;

    return { totalPiezas, piezasListas, planchasSinArchivo, planchasImpresas, planchasCortadas, totalPlanchasImpresion, totalPlanchasCorte, totalPlanchasRepeticiones, impresion1Cara, impresion2Caras, soloCorte, statusSummary, materialSummaryForKpi, materialSummaryForChart, statusSummaryForChart, tareasAbiertas, tareasCompletadas, planchasEnPrepress, tareasSistemaPendientes, planchasDevueltas, planchasHold, planchasParaRevisarPrepress };
  }, [sheets, materialsCatalog, jobData, tasks]);
  
  const renderActiveTab = () => {
    const tabProps = { user, db, storage, jobData, sheets, materialsCatalog, teamMembers, initialSheetToShow, onSheetSelect: setSheetForDetails, tasks };
    switch (activeTab) {
      case 'info': return <GeneralInfoDisplayTab jobData={jobData} clients={clients} setMaximizedImageUrl={setMaximizedImageUrl} />;
      case 'details': return jobData?.type === 'GangRun'
        ? <GangRunTab jobId={jobId} jobData={jobData} db={db} user={user} storage={storage} />
        : <ProductDetailsDisplayTab jobData={jobData} />;
      case 'checklist': return <TasksAndChecklistTab jobId={jobId} user={user} db={db} storage={storage} teamMembers={teamMembers} />;
      case 'files': return <FilesAndDocsDisplayTab jobData={jobData} uploads={docUploads} onUpload={handleUploadDocument} onDeleteDocument={handleDeleteDocument} onUpdateDocument={handleUpdateDocument} />;
      case 'artifacts': return <FileArtifactsTab jobId={jobId} jobData={jobData} db={db} storage={storage} user={user} />;
      case 'design': return <DesignManagerTab {...tabProps} />;
      case 'prepress': return <PrepressTab {...tabProps} />;
      case 'inventory': return <PiecesInventoryTab {...tabProps} />;
      case 'construction': return <ConstructionTab {...tabProps} />;
      case 'installation': return <InstallationTab {...tabProps} />;
      default: return <p>Selecciona una pestaña para ver su contenido.</p>;
    }
  };

  const TabButton = ({ tabName, icon: Icon, label, alert = false, alertColor = 'red' }) => {
    const pulseClass = alertColor === 'yellow'
      ? 'animate-pulse bg-yellow-50 text-yellow-700 border-yellow-400'
      : 'animate-pulse bg-red-50 text-red-600 border-red-400';
    const dotClass = alertColor === 'yellow' ? 'bg-yellow-500' : 'bg-red-500';
    return (
      <button
        onClick={() => setActiveTab(tabName)}
        className={`relative flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${
          activeTab === tabName ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
        } ${alert && activeTab !== tabName ? pulseClass : ''}`}
      >
        <Icon size={16} />
        {label}
        {alert && activeTab !== tabName && (
          <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 ${dotClass} rounded-full animate-ping`} />
        )}
      </button>
    );
  };

  return (
    <>
        <ImageModal imageUrl={maximizedImageUrl} onClose={() => setMaximizedImageUrl(null)} />
        <UploadsWidget uploads={docUploads} onDismiss={dismissUpload} onClearDone={clearDoneUploads} />
        {sheetForDetails && <SheetDetailModal isOpen={true} onClose={() => setSheetForDetails(null)} sheet={sheetForDetails} materialName={materialsCatalog.find(m => m.id === sheetForDetails.materialId)?.name} db={db} storage={storage} jobData={jobData} user={user} />}
        
        <div className="flex flex-col h-screen bg-gray-100">
            <header className="p-3 md:px-5 bg-gray-100 flex-shrink-0">
                {jobData && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <button onClick={() => navigate('/dashboard')} className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"> <ArrowLeft size={16} className="mr-1" /> Volver al Dashboard </button>
                                <h1 className="text-2xl font-bold text-gray-800 truncate">
                                   {jobData.id} / {client?.name || '...'} / {jobData.jobName}
                                </h1>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2 flex-wrap justify-end">
                                {!isBilledOrDone && user?.permissions?.canCreateJobs && (
                                    <button onClick={handleMarkBilling} title="Marcar producción terminada → Pendiente por Facturar" className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100">
                                        <CheckCircle size={16} /> Marcar terminado
                                    </button>
                                )}
                                {canInvoice && (
                                    <button onClick={() => setShowInvoice(true)} title="Subir factura (administración)" className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100">
                                        <FileText size={16} /> {jobData?.invoice ? 'Factura ✓' : 'Factura'}
                                    </button>
                                )}
                                <button onClick={handleDownloadLabel} title="Etiqueta del paquete (PDF con QR)" className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200">
                                    <Archive size={16} /> Etiqueta
                                </button>
                                {hasComponentsForCatalog && (
                                    <button onClick={() => setShowSendComponents(true)} title="Crear/actualizar SKUs en el Catálogo de Productos a partir de los componentes" className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
                                        <Package size={16} /> Enviar al Catálogo
                                    </button>
                                )}
                                <button onClick={handleDownloadJobTicket} disabled={ticketBusy} title="Descargar Job Ticket (PDF) — resumen para producción / consulta offline" className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-gray-700 rounded-lg hover:bg-gray-800 disabled:opacity-50">
                                    <FileText size={16} /> {ticketBusy ? 'Generando…' : 'Job Ticket (PDF)'}
                                </button>
                            </div>
                        </div>
                        <div className={`transition-all duration-500 ease-in-out ${isCockpitVisible ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                            <div className="pt-3">
                                 <JobDashboardHeader 
                                    stats={dashboardStats} 
                                    isLoading={isLoading} 
                                    jobData={jobData}
                                    functions={functions}
                                    db={db}
                                    onShowMissingFileSheet={handleShowMissingFileSheet}
                                    onOpenGeneralAIChat={onOpenGeneralAIChat}
                                    onOpenJobChat={() => setIsJobChatOpen(true)}
                                 />
                            </div>
                        </div>
                    </div>
                )}
            </header>

            <SendComponentsToCatalogModal
                isOpen={showSendComponents}
                onClose={() => setShowSendComponents(false)}
                db={db}
                user={user}
                jobData={jobData}
            />

            {canInvoice && showInvoice && jobData && (
                <InvoiceModal isOpen={showInvoice} onClose={() => setShowInvoice(false)} db={db} storage={storage} user={user} jobData={jobData} onPrepareTicket={uploadJobTicketForEmail} />
            )}

            <JobAiChatModal
                isOpen={isJobChatOpen}
                onClose={() => setIsJobChatOpen(false)}
                jobId={jobId}
                jobName={jobData?.jobName}
                user={user}
            />

            <main className="flex-grow flex flex-col overflow-hidden px-3 md:px-5 pb-3">
                {isLoading && !jobData ? ( 
                    <div className="flex-grow flex items-center justify-center bg-white rounded-lg shadow-sm">
                        <p>Cargando datos del trabajo...</p>
                    </div>
                ) : error ? (
                     <div className="flex-grow flex items-center justify-center bg-white rounded-lg shadow-sm">
                        <p className="text-red-600">{error}</p>
                    </div>
                ) : (
                    <>
                        <div className="flex-shrink-0 border-b border-gray-200 bg-white rounded-t-lg shadow-sm">
                             <div className="flex justify-between items-center">
                                <nav className="flex flex-wrap -mb-px" aria-label="Tabs">
                                    <TabButton tabName="info" icon={Info} label="Información General" />
                                    <TabButton tabName="details" icon={Package} label="Detalles" />
                                    <TabButton tabName="checklist" icon={ListChecks} label="Tareas" alert={dashboardStats.tareasSistemaPendientes > 0} />
                                    <TabButton tabName="artifacts" icon={FileText} label="Archivos" />
                                    <TabButton tabName="design" icon={LayoutGrid} label="Diseño" alert={dashboardStats.planchasDevueltas > 0 || dashboardStats.planchasHold > 0} alertColor={dashboardStats.planchasDevueltas > 0 ? 'red' : 'yellow'} />
                                    <TabButton tabName="prepress" icon={Layers} label="Prepress" alert={dashboardStats.planchasParaRevisarPrepress > 0} alertColor="yellow" />
                                    <TabButton tabName="inventory" icon={Archive} label="Inventario" />
                                    <TabButton tabName="construction" icon={HardHat} label="Construcción" />
                                    <TabButton tabName="installation" icon={Truck} label="Instalación" />
                                </nav>
                                <button 
                                    onClick={() => setIsCockpitVisible(!isCockpitVisible)}
                                    className="p-2 mr-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full"
                                    title={isCockpitVisible ? "Minimizar Cockpit" : "Maximizar Cockpit"}
                                >
                                    {isCockpitVisible ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </button>
                            </div>
                        </div>
                        <div className="flex-grow bg-white p-6 rounded-b-lg shadow-sm overflow-y-auto">
                             {renderActiveTab()}
                        </div>
                    </>
                )}
            </main>
        </div>
    </>
  );
};

export default JobView;
